import { inject, Injectable } from '@angular/core';
import { HttpGatewayService } from './http-gateway.service';
import { Observable, Observer } from 'rxjs';
import {
    Organization,
    OrganizationAccessContract,
    OrganizationDetailsContract,
    OrganizationResponseContract,
    OrganizationType,
} from '../../shared/types/organization';
import { UrlBuilder } from '../../shared/utils/url-utils';
import { CurrencyContract, CurrencyInfo } from '../../shared/types/currency';
import { ApplicationStore, ApplicationStoreType } from '../store/application.store';
import { decodeHtml } from '../../shared/utils/html-utils';

const ORGANIZATION_URLS = {
    GET_USER_ACCESS: 'api/v5/organization/{organizationId}/manage-organization/get-user-access',
    GET_ORGANIZATION_TYPES: 'api/v5/organization/manage-organization/get-org-types',
    GET_ORGANIZATIONS: 'api/v5/organization/manage-organization/get-organizations',
    GET_ORGANIZATION_DETAILS: 'api/v5/organization/{organizationId}/manage-organization/get-org-details',
    GET_CURRENCIES: 'api/v5/organization/manage-organization/get-currencies',
    UPDATE_ORGANIZATION: 'api/v5/organization/{organizationId}/manage-organization/update-org',
    UPDATE_ORGANIZATION_MFA_STATUS: 'api/v5/organization/{organizationId}/manage-organization/update-mfa-status',
    DELETE_ORGANIZATION: 'api/v5/organization/{organizationId}/manage-organization/delete-org',
};

@Injectable({ providedIn: 'root' })
export class OrganizationService {
    private http = inject(HttpGatewayService);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);

    public getOrganizations(): Observable<Organization[]> {
        return new Observable<Organization[]>((observer: Observer<Organization[]>) => {
            const availableOrganizations = this.applicationStore.organizations();

            if (!availableOrganizations || availableOrganizations.length === 0) {
                this.http.get<OrganizationResponseContract>(ORGANIZATION_URLS.GET_ORGANIZATIONS).subscribe({
                    next: (response: OrganizationResponseContract) => {
                        const organizations = response.organizations
                            .map((org) => ({
                                id: org.id,
                                name: org.name,
                                type: org.type,
                                chainId: org.chain_id,
                                fromV3: org.from_v3,
                                createdAt: org.created_at,
                                default: org.default,
                            }))
                            .sort((a, b) => a.name.localeCompare(b.name));

                        this.applicationStore.setOrganizations(organizations);

                        observer.next(organizations);
                        observer.complete();
                    },
                    error: (error) => {
                        observer.next([]);
                        observer.complete();
                    },
                });
            } else {
                observer.next(availableOrganizations);
                observer.complete();
            }
        });
    }

    public getOrganizationTypes(): Observable<OrganizationType[]> {
        return new Observable<OrganizationType[]>((observer: Observer<OrganizationType[]>) => {
            const availableTypes = this.applicationStore.organizationTypes();

            if (!availableTypes || availableTypes.length === 0) {
                this.http.get<OrganizationType[]>(ORGANIZATION_URLS.GET_ORGANIZATION_TYPES).subscribe({
                    next: (response: OrganizationType[]) => {
                        this.applicationStore.setOrganizationTypes(response);

                        observer.next(response);
                        observer.complete();
                    },
                    error: (error) => {
                        observer.next([]);
                        observer.complete();
                    },
                });
            } else {
                observer.next(availableTypes);
                observer.complete();
            }
        });
    }

    public selectOrganization(organization: Organization): void {
        const alreadyResolved =
            this.applicationStore.selectedOrganization()?.id === organization.id &&
            this.applicationStore.organizationAccessResolved();
        if (alreadyResolved) return;

        this.applicationStore.setSelectedOrganization(organization);
        this.getUserAccess(organization.id).subscribe({
            next: (access) => {
                if (this.applicationStore.selectedOrganization()?.id !== organization.id) return;
                this.applicationStore.setSelectedOrganizationAccess(access);
            },
            error: () => {
                if (this.applicationStore.selectedOrganization()?.id !== organization.id) return;
                this.applicationStore.setSelectedOrganizationAccess(null);
            },
        });
    }

    public getUserAccess(organizationId: string): Observable<OrganizationAccessContract> {
        const url = new UrlBuilder(ORGANIZATION_URLS.GET_USER_ACCESS).addOrganizationId(organizationId).toUrl();
        return this.http.get(url);
    }

    public getCurrencies(): Observable<CurrencyInfo[]> {
        return new Observable<CurrencyInfo[]>((observer: Observer<CurrencyInfo[]>) => {
            const availableCurrencies = this.applicationStore.currencies();

            if (!availableCurrencies || availableCurrencies.length === 0) {
                this.http.get<CurrencyContract[]>(ORGANIZATION_URLS.GET_CURRENCIES).subscribe({
                    next: (contracts) => {
                        const currencies = contracts.map((c) => new CurrencyInfo(c.code, decodeHtml(c.html)));
                        this.applicationStore.setCurrencies(currencies);
                        observer.next(currencies);
                        observer.complete();
                    },
                    error: () => {
                        observer.next([]);
                        observer.complete();
                    },
                });
            } else {
                observer.next(availableCurrencies);
                observer.complete();
            }
        });
    }

    public getOrganizationDetails(organizationId: string): Observable<OrganizationDetailsContract> {
        const url = new UrlBuilder(ORGANIZATION_URLS.GET_ORGANIZATION_DETAILS)
            .addOrganizationId(organizationId)
            .toUrl();

        return this.http.get(url);
    }

    public updateOrganization(organizationId: string, name: string, type: string, currency: string): Observable<void> {
        const request = {
            name,
            type,
            currency,
        };
        const url = new UrlBuilder(ORGANIZATION_URLS.UPDATE_ORGANIZATION).addOrganizationId(organizationId).toUrl();

        return this.http.put(url, request);
    }

    public updateOrganizationMfaStatus(organizationId: string, mfaEnabled: boolean): Observable<void> {
        const request = {
            mfaEnabled,
        };
        const url = new UrlBuilder(ORGANIZATION_URLS.UPDATE_ORGANIZATION_MFA_STATUS)
            .addOrganizationId(organizationId)
            .toUrl();

        return this.http.put(url, request);
    }

    public deleteOrganization(organizationId: string): Observable<any> {
        const url = new UrlBuilder(ORGANIZATION_URLS.DELETE_ORGANIZATION).addOrganizationId(organizationId).toUrl();

        return this.http.delete(url);
    }
}
